import React, { useState, useEffect } from 'react';
import { CreditCard, ArrowUpRight, ArrowDownRight, CheckCircle, XCircle } from 'lucide-react';
import * as billingService from '../services/billingService';

const CREDIT_PACKS = [
  { cents: 500, label: '$5', credits: '500 credits' },
  { cents: 1000, label: '$10', credits: '1,000 credits' },
  { cents: 2500, label: '$25', credits: '2,500 credits' },
  { cents: 5000, label: '$50', credits: '5,000 credits' },
  { cents: 10000, label: '$100', credits: '10,000 credits' },
];

const PaymentPage = () => {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [txPage, setTxPage] = useState(0);
  const [filterType, setFilterType] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchData();

    // Check for Stripe redirect success/cancel
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccessMessage('Payment successful! Credits have been added to your account.');
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [txPage, filterType]);

  const fetchData = async () => {
    try {
      const res = await billingService.getBalance();
      if (res.success) setBalance(res.balance_cents);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await billingService.getTransactions({
        limit: 20,
        offset: txPage * 20,
        type: filterType,
      });
      if (res.success) setTransactions(res.transactions || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  const handleBuy = async (cents) => {
    setPurchasing(cents);
    try {
      const res = await billingService.createCheckoutSession(cents);
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        alert(res.error || 'Failed to create checkout session');
      }
    } catch (err) {
      alert('Failed to initiate checkout');
    } finally {
      setPurchasing(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const typeColors = {
    purchase: 'text-green-600 bg-green-50',
    inference_debit: 'text-red-600 bg-red-50',
    admin_grant: 'text-blue-600 bg-blue-50',
    refund: 'text-orange-600 bg-orange-50',
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Payment & Credits</h2>

      {successMessage && (
        <div className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg">
          <CheckCircle size={20} />
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-600 hover:text-green-800">
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* Balance + Buy */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Current Balance</h3>
          <div className="text-3xl font-bold text-gray-900">
            {loading ? '...' : `$${((balance || 0) / 100).toFixed(2)}`}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? '' : `${balance || 0} credits`}
          </p>
        </div>

        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow border">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Buy Credits</h3>
          <div className="flex flex-wrap gap-3">
            {CREDIT_PACKS.map(pack => (
              <button
                key={pack.cents}
                onClick={() => handleBuy(pack.cents)}
                disabled={purchasing !== null}
                className={`flex-1 min-w-[100px] border-2 rounded-lg p-3 text-center transition-all ${
                  purchasing === pack.cents
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                } disabled:opacity-50`}
              >
                <div className="text-lg font-bold text-gray-900">{pack.label}</div>
                <div className="text-xs text-gray-500">{pack.credits}</div>
                {purchasing === pack.cents && (
                  <div className="text-xs text-purple-600 mt-1">Redirecting...</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Transaction History</h3>
          <div className="flex space-x-2">
            {[null, 'purchase', 'inference_debit', 'admin_grant'].map(type => (
              <button
                key={type || 'all'}
                onClick={() => { setFilterType(type); setTxPage(0); }}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filterType === type
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === null ? 'All' : type === 'purchase' ? 'Purchases' : type === 'inference_debit' ? 'Inference' : 'Grants'}
              </button>
            ))}
          </div>
        </div>

        {transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CreditCard size={32} className="mx-auto mb-2 opacity-50" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {transactions.map(tx => (
              <div key={tx.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  {tx.amount_cents > 0
                    ? <ArrowUpRight size={18} className="text-green-500" />
                    : <ArrowDownRight size={18} className="text-red-500" />
                  }
                  <div>
                    <p className="text-sm font-medium text-gray-900">{tx.description || tx.type}</p>
                    <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-semibold ${tx.amount_cents > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.amount_cents > 0 ? '+' : ''}{tx.amount_cents} credits
                  </span>
                  <p className="text-xs text-gray-400">bal: {tx.balance_after_cents}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {transactions.length >= 20 && (
          <div className="p-3 border-t flex justify-between">
            <button
              onClick={() => setTxPage(p => Math.max(0, p - 1))}
              disabled={txPage === 0}
              className="text-sm text-purple-600 disabled:text-gray-400"
            >
              Previous
            </button>
            <button
              onClick={() => setTxPage(p => p + 1)}
              className="text-sm text-purple-600"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
